import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { ChartBar, Trophy, Target, TennisBall, UserCircle, Broadcast, Medal } from "@phosphor-icons/react";
import { CATEGORIES, categoryShort } from "@/lib/locations";

const COLORS = ["#CCFF00", "#FF3B30", "#007AFF", "#FFB800", "#34C759"];

export default function Statistics() {
  const { user } = useAuth();
  const [ranking, setRanking] = useState([]);
  const [rankingCategory, setRankingCategory] = useState("all");
  const [myStats, setMyStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [matchSummary, setMatchSummary] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const [completedMatches, setCompletedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceTab, setSourceTab] = useState("all");
  const [sourceRanking, setSourceRanking] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceClub, setSourceClub] = useState("all");
  const [sourceCat, setSourceCat] = useState("all");
  const [sourceLeague, setSourceLeague] = useState("all");
  const [sourceRetaDay, setSourceRetaDay] = useState("all");
  const [sourceTournament, setSourceTournament] = useState("all");
  const [sourceRentalDate, setSourceRentalDate] = useState("");
  const [tournamentsList, setTournamentsList] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [recalcBusy, setRecalcBusy] = useState(false);
  const [topMetric, setTopMetric] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/statistics/ranking"),
      api.get(`/statistics/player/${user?.id}`),
      api.get(`/statistics/player/${user?.id}/history`),
      api.get("/statistics/matches-summary"),
      api.get("/statistics/all-players"),
      api.get("/statistics/matches-completed"),
    ]).then(([r, s, h, ms, ap, cm]) => {
      setRanking(r.data);
      setMyStats(s.data);
      setHistory(h.data);
      setMatchSummary(ms.data);
      setAllPlayers(ap.data);
      setCompletedMatches(cm.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id]);

  useEffect(() => {
    const q = rankingCategory === "all" ? "" : `?category=${rankingCategory}`;
    api.get(`/statistics/ranking${q}`).then(({ data }) => setRanking(data)).catch(() => {});
  }, [rankingCategory]);

  useEffect(() => {
    api.get("/clubs").then(({ data }) => setClubs(data || [])).catch(() => {});
    api.get("/leagues").then(({ data }) => setLeagues(data || [])).catch(() => {});
    api.get("/tournaments").then(({ data }) => setTournamentsList(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setSourceLoading(true);
    const params = new URLSearchParams();
    params.set("source", sourceTab);
    if (sourceClub !== "all") params.set("club_id", sourceClub);
    if (sourceCat !== "all") params.set("category", sourceCat);
    if (sourceLeague !== "all") params.set("league_id", sourceLeague);
    if (sourceRetaDay !== "all") params.set("reta_day", sourceRetaDay);
    if (sourceTab === "tournament" && sourceTournament !== "all") params.set("tournament_id", sourceTournament);
    if (sourceTab === "rental" && sourceRentalDate) params.set("rental_date", sourceRentalDate);
    api.get(`/statistics/ranking-by-source?${params.toString()}`)
      .then(({ data }) => setSourceRanking(data.ranking || []))
      .catch(() => setSourceRanking([]))
      .finally(() => setSourceLoading(false));
  }, [sourceTab, sourceClub, sourceCat, sourceLeague, sourceRetaDay, sourceTournament, sourceRentalDate]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  const winRate = myStats?.games_played > 0 ? Math.round((myStats.games_won / myStats.games_played) * 100) : 0;
  const lossRate = 100 - winRate;
  const pieData = myStats?.games_played > 0 ? [
    { name: "Victorias", value: myStats.games_won },
    { name: "Derrotas", value: myStats.games_played - myStats.games_won }
  ] : [];

  // Top 15 drill-down metrics (computed from ranking data already loaded)
  const TOP15_METRICS = [
    { key: "points", label: "Puntos", emoji: "🏆", fmt: (v) => v },
    { key: "games_won", label: "Partidos Ganados", emoji: "🥇", fmt: (v) => v },
    { key: "games_played", label: "Partidos Jugados", emoji: "🎾", fmt: (v) => v },
    { key: "win_rate", label: "% Victoria", emoji: "📈", fmt: (v) => `${v}%` },
    { key: "aces", label: "Aces", emoji: "💥", fmt: (v) => v },
    { key: "winners", label: "Winners", emoji: "⚡", fmt: (v) => v },
    { key: "balls_hit", label: "Bolas Golpeadas", emoji: "🎯", fmt: (v) => v },
  ];
  const enrichedRanking = (ranking || []).map((r) => ({
    ...r,
    win_rate: r.games_played > 0 ? Math.round((r.games_won / r.games_played) * 100) : 0,
  }));
  const top15ByMetric = (metricKey) =>
    [...enrichedRanking]
      .filter((r) => (r[metricKey] || 0) > 0)
      .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
      .slice(0, 15);

  const chartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[#1A1A1A] border border-white/10 p-3 text-xs">
        <p className="text-gray-400 mb-1">{label}</p>
        {payload.map((p, i) => <p key={i} style={{ color: p.color || p.fill }} className="font-semibold">{p.name}: {p.value}</p>)}
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="statistics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Rendimiento</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Estadisticas</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {(user?.role === "admin" || user?.role === "directivo") && (
            <ExportPdfMenu />
          )}
          <Link to="/dashboard/estadisticas/h2h" className="inline-flex items-center gap-2 bg-[#CCFF00]/10 text-[#CCFF00] hover:bg-[#CCFF00]/20 border border-[#CCFF00]/30 px-4 h-10 text-xs font-bold uppercase tracking-wider transition-colors" data-testid="h2h-link">
            <Broadcast size={14} /> Comparar H2H
          </Link>
        </div>
      </div>

      {/* TOP 15 — Drill-down rápido por métrica */}
      <div className="card-tactical p-4" data-testid="top15-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00] flex items-center gap-2"><Medal size={12} weight="fill" /> Top 15</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-black uppercase tracking-tight text-white">Estadísticas resumidas</h2>
          </div>
          {topMetric && (
            <button onClick={() => setTopMetric(null)} className="text-xs text-gray-400 hover:text-[#CCFF00] underline" data-testid="top15-clear">← Ver todas las métricas</button>
          )}
        </div>

        {!topMetric ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {TOP15_METRICS.map((m) => {
              const top3 = top15ByMetric(m.key).slice(0, 3);
              return (
                <button key={m.key} onClick={() => setTopMetric(m.key)}
                  className="text-left bg-[#0A0A0A] border border-white/10 hover:border-[#CCFF00]/40 p-3 rounded transition-all"
                  data-testid={`top15-tile-${m.key}`}>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{m.emoji} {m.label}</p>
                  {top3.length === 0 ? (
                    <p className="text-xs text-gray-600 mt-2">Sin datos</p>
                  ) : (
                    <div className="mt-2 space-y-0.5">
                      {top3.map((r, i) => (
                        <div key={r.user_id} className="flex items-baseline justify-between text-xs">
                          <span className={`truncate font-semibold ${i === 0 ? "text-yellow-400" : i === 1 ? "text-gray-300" : "text-orange-400"}`}>{i + 1}. {r.name?.split(" ")[0]}</span>
                          <span className="font-mono font-bold text-[#CCFF00]">{m.fmt(r[m.key] || 0)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[#CCFF00] mt-2 uppercase tracking-widest">Ver Top 15 →</p>
                </button>
              );
            })}
          </div>
        ) : (
          <div data-testid={`top15-panel-${topMetric}`}>
            {(() => {
              const m = TOP15_METRICS.find((x) => x.key === topMetric);
              const list = top15ByMetric(topMetric);
              return (
                <>
                  <p className="text-sm text-white font-semibold mb-2">{m.emoji} {m.label} — Top 15</p>
                  {list.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6 text-center">Sin datos para esta métrica.</p>
                  ) : (
                    <div className="overflow-hidden border border-white/5">
                      <div className="grid grid-cols-[50px_1fr_120px_120px] gap-2 px-3 py-2 bg-white/5 text-[9px] tracking-[0.2em] uppercase font-bold text-gray-400">
                        <span>#</span><span>Jugador</span><span className="text-right">{m.label}</span><span className="text-right">Partidos</span>
                      </div>
                      <div className="divide-y divide-white/5">
                        {list.map((r, i) => {
                          const medal = i === 0 ? "text-yellow-400" : i === 1 ? "text-gray-300" : i === 2 ? "text-orange-400" : "text-gray-500";
                          return (
                            <Link key={r.user_id} to={`/dashboard/jugador/${r.user_id}`} className="grid grid-cols-[50px_1fr_120px_120px] gap-2 px-3 py-2 items-center text-xs hover:bg-white/5 transition-colors">
                              <span className={`font-['Barlow_Condensed'] text-2xl font-black ${medal}`}>#{i + 1}</span>
                              <div className="min-w-0">
                                <p className="text-white font-semibold truncate">{r.name}</p>
                                <p className="text-[9px] text-gray-500 uppercase">{r.role}{r.category ? ` · Cat ${r.category}` : ""}</p>
                              </div>
                              <span className="text-right font-mono font-bold text-[#CCFF00]">{m.fmt(r[topMetric] || 0)}</span>
                              <span className="text-right font-mono text-gray-400">{r.games_played || 0}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Match Statistics (finished scoreboards) */}
      <div data-testid="match-statistics">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Broadcast size={14} /> Marcadores Terminados</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight">Estadistica Global</h2>
          </div>
          <Link to="/dashboard/marcador" className="text-xs uppercase tracking-wider text-[#CCFF00] hover:underline">Ver marcadores →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Partidos Terminados", value: matchSummary?.total_matches || 0, icon: Medal, color: "#CCFF00" },
            { label: "Sets Totales", value: matchSummary?.total_sets || 0, icon: ChartBar, color: "#007AFF" },
            { label: "Juegos Totales", value: matchSummary?.total_games || 0, icon: TennisBall, color: "#34C759" },
            { label: "Tiebreaks", value: matchSummary?.tiebreaks || 0, icon: Target, color: "#FFB800" },
          ].map((s) => (
            <div key={s.label} className="card-tactical p-6" data-testid={`match-stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <s.icon size={22} weight="duotone" style={{ color: s.color }} className="mb-2" />
              <p className="font-['Barlow_Condensed'] text-4xl font-black text-white">{s.value}</p>
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
        {matchSummary && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div className="card-tactical p-5" data-testid="summary-by-format">
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Por Formato</p>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm"><span className="text-white">2 de 3 Sets</span><span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{matchSummary.by_format?.standard || 0}</span></div>
                <div className="flex justify-between items-center text-sm"><span className="text-white">Tabla 4-0</span><span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{matchSummary.by_format?.short || 0}</span></div>
                <div className="flex justify-between items-center text-sm border-t border-white/5 pt-2"><span className="text-gray-400">Promedio sets/partido</span><span className="font-['JetBrains_Mono'] text-white">{matchSummary.avg_sets_per_match}</span></div>
              </div>
            </div>
            <div className="card-tactical p-5" data-testid="summary-by-category">
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Por Categoria</p>
              {matchSummary.by_category?.length ? matchSummary.by_category.map((c) => (
                <div key={c.category} className="flex justify-between items-center text-sm mb-1.5">
                  <span className="text-white uppercase">{c.category === "sin_cat" ? "Sin categoria" : `Cat. ${c.category}`}</span>
                  <span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{c.count}</span>
                </div>
              )) : <p className="text-gray-500 text-xs">Sin datos</p>}
            </div>
            <div className="card-tactical p-5" data-testid="summary-by-club">
              <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Por Club</p>
              {matchSummary.by_club?.length ? matchSummary.by_club.slice(0, 5).map((c) => (
                <div key={c.club_id} className="flex justify-between items-center text-sm mb-1.5">
                  <span className="text-white truncate">{c.name}</span>
                  <span className="font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{c.count}</span>
                </div>
              )) : <p className="text-gray-500 text-xs">Sin datos</p>}
            </div>
          </div>
        )}
      </div>

      {/* Format reference panel */}
      <div className="card-tactical p-6" data-testid="formats-panel">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Formatos</p>
        <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4">Reglas de Puntuacion</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#0A0A0A] border border-white/10 p-5" data-testid="format-short-card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-['Barlow_Condensed'] text-xl font-black text-[#CCFF00] uppercase">Tabla 4-0 · Sets a 4</p>
              <span className="text-[10px] font-['JetBrains_Mono'] text-gray-500 border border-white/10 px-2 py-0.5">CORTO</span>
            </div>
            <ul className="text-sm text-gray-300 space-y-1 mb-4">
              <li>· Mejor de 3 sets</li>
              <li>· Set se gana con 4 juegos (margen de 2)</li>
              <li>· En 3-3 se juega tiebreak a 7 puntos</li>
            </ul>
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center text-sm pt-3 border-t border-white/5">
              <span className="text-gray-500 text-[10px] uppercase tracking-widest">Ejemplo</span>
              <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">4</span>
              <span className="text-gray-600">-</span>
              <span className="font-['Barlow_Condensed'] text-2xl font-black text-gray-500">3</span>
              <span className="text-[10px] font-['JetBrains_Mono'] text-orange-400 bg-orange-500/10 px-1.5 py-0.5 border border-orange-500/30">TB 7-5</span>
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-5" data-testid="format-standard-card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-['Barlow_Condensed'] text-xl font-black text-[#CCFF00] uppercase">2 de 3 Sets · Sets a 6</p>
              <span className="text-[10px] font-['JetBrains_Mono'] text-gray-500 border border-white/10 px-2 py-0.5">ESTANDAR</span>
            </div>
            <ul className="text-sm text-gray-300 space-y-1 mb-4">
              <li>· Mejor de 3 sets</li>
              <li>· Set se gana con 6 juegos (margen de 2)</li>
              <li>· En 6-6 se juega tiebreak a 7 puntos</li>
            </ul>
            <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center text-sm pt-3 border-t border-white/5">
              <span className="text-gray-500 text-[10px] uppercase tracking-widest">Ejemplo</span>
              <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">6</span>
              <span className="text-gray-600">-</span>
              <span className="font-['Barlow_Condensed'] text-2xl font-black text-gray-500">4</span>
              <span className="text-[10px] font-['JetBrains_Mono'] text-gray-400 bg-white/5 px-1.5 py-0.5 border border-white/10">6-3</span>
            </div>
          </div>
        </div>
        <div className="mt-4 bg-[#0A0A0A] border border-white/10 p-5" data-testid="scoring-panel">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Puntuacion por Juego</p>
          <div className="flex items-center gap-2 flex-wrap font-['Barlow_Condensed'] font-black uppercase">
            {["0", "15", "30", "40", "AD", "JUEGO"].map((v, i) => (
              <div key={v} className="flex items-center gap-2">
                <span className={`px-3 py-1.5 text-lg border ${v === "JUEGO" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-white border-white/10"}`}>{v}</span>
                {i < 5 && <span className="text-gray-600">→</span>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-3">Empate a 40 → Deuce → siguiente punto es Ventaja (AD). Quien tenga ventaja y gane el punto siguiente gana el juego.</p>
        </div>
      </div>

      {/* All players (registered + unregistered) */}
      <div className="card-tactical p-6" data-testid="all-players-stats">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Jugadores</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight">Ranking Completo (registrados + invitados)</h2>
            <p className="text-xs text-gray-500 mt-1">Incluye nombres agregados manualmente aunque no esten registrados</p>
          </div>
        </div>
        {allPlayers.length === 0 ? <p className="text-gray-500 text-sm">Sin jugadores aun</p> : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">#</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Jugador</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Club</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Puntos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Partidos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">V-D</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Sets</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Juegos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allPlayers.map((p, i) => (
                <TableRow key={`${p.name}-${i}`} className="border-white/5 hover:bg-white/5" data-testid={`all-player-row-${i}`}>
                  <TableCell className="font-['Barlow_Condensed'] text-xl font-black text-gray-600">{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell>
                    {p.user_id ? (
                      <Link to={`/dashboard/jugador/${p.user_id}`} className="font-semibold text-white hover:text-[#CCFF00]">{p.name}</Link>
                    ) : (
                      <span className="font-semibold text-gray-300">{p.name} <span className="text-[9px] text-gray-600 ml-1 uppercase tracking-wider">invitado</span></span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-gray-400">{p.clubs?.[0]?.name || "—"}{p.clubs?.length > 1 ? ` +${p.clubs.length - 1}` : ""}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-[#CCFF00] font-bold">{p.points}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{p.matches}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-300"><span className="text-[#CCFF00]">{p.wins}</span>-<span className="text-red-400">{p.losses}</span></TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{p.sets_won}-{p.sets_lost}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{p.games_won}-{p.games_lost}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* All completed scoreboards with full set scores */}
      <div className="card-tactical p-6" data-testid="completed-scoreboards">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1 flex items-center gap-2"><Broadcast size={14} /> Historial</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight">Todos los Marcadores Terminados</h2>
          </div>
          <span className="text-xs text-gray-500 font-['JetBrains_Mono']">{completedMatches.length} partidos</span>
        </div>
        {completedMatches.length === 0 ? <p className="text-gray-500 text-sm">Sin marcadores terminados</p> : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Fecha</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Club</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Formato</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Cat</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Parejas</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Sets</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Final</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {completedMatches.map((m) => {
                const setScores = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}${s.tiebreak ? ` (${s.tiebreak.pair1}-${s.tiebreak.pair2})` : ""}`);
                return (
                  <TableRow key={m.id} className="border-white/5 hover:bg-white/5" data-testid={`completed-match-${m.id}`}>
                    <TableCell className="text-xs text-gray-400 font-['JetBrains_Mono'] whitespace-nowrap">{m.date}{m.time ? ` · ${m.time}` : ""}</TableCell>
                    <TableCell className="text-sm text-white whitespace-nowrap">{m.club_name || "—"}</TableCell>
                    <TableCell className="text-[10px] uppercase text-gray-400">{m.format === "short" ? "Tabla 4-0" : "2 de 3 Sets"}</TableCell>
                    <TableCell className="text-[10px] uppercase text-[#CCFF00]">{m.category || "—"}</TableCell>
                    <TableCell>
                      <Link to={`/dashboard/marcador/${m.id}`} className="block hover:text-[#CCFF00]">
                        <p className={`text-sm ${m.winner_pair === 1 ? "text-[#CCFF00] font-semibold" : "text-white"}`}>{m.pair1.p1_name} / {m.pair1.p2_name}</p>
                        <p className={`text-sm ${m.winner_pair === 2 ? "text-[#CCFF00] font-semibold" : "text-white"}`}>{m.pair2.p1_name} / {m.pair2.p2_name}</p>
                      </Link>
                    </TableCell>
                    <TableCell className="font-['JetBrains_Mono'] text-sm">
                      {setScores.map((s, i) => <div key={i} className="text-gray-300">{s}</div>)}
                    </TableCell>
                    <TableCell className="text-right font-['Barlow_Condensed'] font-black text-2xl">
                      <span className={m.winner_pair === 1 ? "text-[#CCFF00]" : "text-gray-500"}>{m.sets_won.pair1}</span>
                      <span className="text-gray-600 mx-0.5">-</span>
                      <span className={m.winner_pair === 2 ? "text-[#CCFF00]" : "text-gray-500"}>{m.sets_won.pair2}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* My Stats Bento */}
      <div data-testid="my-stats-grid">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-4">Mis Estadisticas</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Puntos", value: myStats?.points || 0, icon: Target, color: "#CCFF00" },
            { label: "Partidos", value: myStats?.games_played || 0, icon: ChartBar, color: "#007AFF" },
            { label: "Victorias", value: myStats?.games_won || 0, icon: Trophy, color: "#FFB800" },
            { label: "Bolas", value: myStats?.balls_hit || 0, icon: TennisBall, color: "#34C759" },
          ].map((s) => (
            <div key={s.label} className="card-tactical p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
              <s.icon size={24} weight="duotone" style={{ color: s.color }} className="mb-3" />
              <p className="font-['Barlow_Condensed'] text-4xl font-black text-white">{s.value}</p>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          {/* Win Rate + Ranking */}
          <div className="card-tactical p-6" data-testid="win-rate-card">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-2">% Victoria</p>
            <div className="flex items-end gap-3">
              <span className="font-['Barlow_Condensed'] text-5xl font-black text-[#CCFF00]">{winRate}%</span>
              <span className="text-gray-600 text-sm mb-1">Ranking #{myStats?.ranking || "-"}</span>
            </div>
            <div className="w-full bg-[#0A0A0A] h-2 mt-4">
              <div className="bg-[#CCFF00] h-2 transition-all duration-500" style={{ width: `${winRate}%` }} />
            </div>
          </div>

          {/* Points Trend */}
          {history.length > 0 && (
            <div className="card-tactical p-6" data-testid="points-trend-chart">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-3">Tendencia de Puntos</p>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={history}>
                  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#CCFF00" stopOpacity={0.3}/><stop offset="95%" stopColor="#CCFF00" stopOpacity={0}/></linearGradient></defs>
                  <XAxis dataKey="date" tick={false} axisLine={false} />
                  <YAxis tick={false} axisLine={false} />
                  <Tooltip content={chartTooltip} />
                  <Area type="monotone" dataKey="points" name="Puntos" stroke="#CCFF00" fill="url(#grad1)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Pie Chart */}
          {pieData.length > 0 && (
            <div className="card-tactical p-6" data-testid="pie-chart">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 mb-3">Distribucion</p>
              <ResponsiveContainer width="100%" height={120}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" strokeWidth={0}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip content={chartTooltip} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2">
                {pieData.map((d, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs text-gray-400">
                    <span className="w-2 h-2 inline-block" style={{ backgroundColor: COLORS[i] }} /> {d.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Win Rate Over Time Bar Chart */}
        {history.length > 1 && (
          <div className="card-tactical p-6 mt-4" data-testid="winrate-bar-chart">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Historico</p>
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight mb-4">Victorias vs Derrotas Acumuladas</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#737373" }} />
                <YAxis tick={{ fontSize: 10, fill: "#737373" }} />
                <Tooltip content={chartTooltip} />
                <Bar dataKey="wins" name="Victorias" fill="#CCFF00" />
                <Bar dataKey="losses" name="Derrotas" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Ranking por Origen */}
      <div className="card-tactical p-6" data-testid="ranking-by-source">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Ranking por origen</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">
              {sourceTab === "all" && "Todos los partidos"}
              {sourceTab === "tournament" && "Solo Torneos"}
              {sourceTab === "reta" && "Solo Retas"}
              {sourceTab === "rental" && "Solo Rentas de cancha"}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="source-tabs">
          {[
            { v: "all", label: "Global" },
            { v: "tournament", label: "Torneos" },
            { v: "reta", label: "Retas" },
            { v: "rental", label: "Rentas" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setSourceTab(t.v)}
              className={`px-3 h-8 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${sourceTab === t.v ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`}
              data-testid={`source-tab-${t.v}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filtros Club/Liga/Categoria/Reta del dia */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3" data-testid="source-filters">
          <select value={sourceClub} onChange={(e) => setSourceClub(e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-3 h-9 cursor-pointer hover:border-[#CCFF00]/30" data-testid="source-filter-club">
            <option value="all">Todos los clubes</option>
            {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={sourceCat} onChange={(e) => setSourceCat(e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-3 h-9 cursor-pointer hover:border-[#CCFF00]/30" data-testid="source-filter-cat">
            <option value="all">Todas las categorias</option>
            <option value="6">6ta</option><option value="5">5ta</option><option value="4">4ta</option>
            <option value="3">3ra</option><option value="2">2da</option><option value="1">1ra</option>
            <option value="open">Open</option>
          </select>
          <select value={sourceLeague} onChange={(e) => setSourceLeague(e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-3 h-9 cursor-pointer hover:border-[#CCFF00]/30" data-testid="source-filter-league">
            <option value="all">Todas las ligas</option>
            {leagues.map((lg) => <option key={lg.id} value={lg.id}>{lg.name}</option>)}
          </select>
          <select value={sourceRetaDay} onChange={(e) => setSourceRetaDay(e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-3 h-9 cursor-pointer hover:border-[#CCFF00]/30" data-testid="source-filter-retaday">
            <option value="all">Retas: cualquier dia</option>
            <option value="lunes">Lunes</option><option value="miercoles">Miercoles</option>
            <option value="viernes">Viernes</option><option value="domingo">Domingo</option>
          </select>
        </div>

        {/* Filtros contextuales por tab */}
        {sourceTab === "tournament" && (
          <div className="mb-4" data-testid="source-filter-tournament-row">
            <select value={sourceTournament} onChange={(e) => setSourceTournament(e.target.value)} className="w-full bg-[#0A0A0A] border border-purple-500/30 text-white text-xs px-3 h-9 cursor-pointer hover:border-purple-500/60" data-testid="source-filter-tournament">
              <option value="all">Todos los torneos</option>
              {tournamentsList.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.start_date ? ` · ${t.start_date}` : ""}{t.category ? ` · Cat ${t.category}` : ""}</option>
              ))}
            </select>
          </div>
        )}
        {sourceTab === "rental" && (
          <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="source-filter-rental-row">
            <label className="text-[10px] uppercase tracking-widest text-blue-300 font-semibold">Fecha de renta:</label>
            <input type="date" value={sourceRentalDate} onChange={(e) => setSourceRentalDate(e.target.value)} className="bg-[#0A0A0A] border border-blue-500/30 text-white text-xs px-3 h-9 cursor-pointer hover:border-blue-500/60" data-testid="source-filter-rental-date" />
            {sourceRentalDate && (
              <button onClick={() => setSourceRentalDate("")} className="text-[10px] uppercase text-gray-400 hover:text-white" data-testid="source-filter-rental-date-clear">Limpiar</button>
            )}
          </div>
        )}

        {user?.role === "admin" && (
          <div className="flex justify-end gap-2 mb-4 flex-wrap">
            <button
              disabled={recalcBusy}
              onClick={async () => {
                setRecalcBusy(true);
                try {
                  const { data } = await api.post("/admin/backfill-match-classification");
                  const a = data.reta_day_assignments || {};
                  toast.success(`${data.matches_updated} partidos reclasificados · Lun:${a.lunes||0} Mie:${a.miercoles||0} Vie:${a.viernes||0} Dom:${a.domingo||0}`);
                  // Refresh current ranking view
                  const params = new URLSearchParams();
                  params.set("source", sourceTab);
                  if (sourceClub !== "all") params.set("club_id", sourceClub);
                  if (sourceCat !== "all") params.set("category", sourceCat);
                  if (sourceLeague !== "all") params.set("league_id", sourceLeague);
                  if (sourceRetaDay !== "all") params.set("reta_day", sourceRetaDay);
                  if (sourceTab === "tournament" && sourceTournament !== "all") params.set("tournament_id", sourceTournament);
                  if (sourceTab === "rental" && sourceRentalDate) params.set("rental_date", sourceRentalDate);
                  const { data: r } = await api.get(`/statistics/ranking-by-source?${params.toString()}`);
                  setSourceRanking(r.ranking || []);
                } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                finally { setRecalcBusy(false); }
              }}
              className="inline-flex items-center gap-2 bg-[#141414] border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10 px-3 h-8 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40"
              data-testid="backfill-classification-btn"
            >
              {recalcBusy ? "Procesando..." : "↳ Reclasificar partidos"}
            </button>
            <button
              disabled={recalcBusy}
              onClick={async () => {
                setRecalcBusy(true);
                try {
                  const { data } = await api.post("/admin/recalculate-stats");
                  toast.success(data.message || "Recalculado");
                } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
                finally { setRecalcBusy(false); }
              }}
              className="inline-flex items-center gap-2 bg-[#141414] border border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 px-3 h-8 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40"
              data-testid="recalc-stats-btn"
            >
              {recalcBusy ? "Recalculando..." : "↻ Recalcular estadisticas"}
            </button>
          </div>
        )}
        {sourceLoading ? (
          <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
        ) : sourceRanking.length === 0 ? (
          <p className="text-gray-500 text-sm">No hay partidos validados en este origen aun</p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4" data-testid="source-ranking-scroll">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">#</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Jugador</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Puntos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Partidos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">V</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">D</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sourceRanking.slice(0, 50).map((r, i) => {
                const isTop = i < 3;
                const nameCls = isTop ? "font-semibold text-green-400" : "font-semibold text-white";
                return (
                <TableRow key={r.user_id || r.name + i} className={`border-white/5 hover:bg-white/5 ${isTop ? "bg-green-500/5" : ""}`} data-testid={`source-row-${i}`}>
                  <TableCell className={`font-['Barlow_Condensed'] text-xl font-black ${isTop ? "text-green-400" : "text-gray-600"}`}>{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell>
                    <span className={nameCls}>{r.name}</span>
                    {r.is_guest && <span className="ml-2 text-[9px] uppercase tracking-widest text-yellow-400 border border-yellow-400/30 px-1.5 py-0.5">inv</span>}
                  </TableCell>
                  <TableCell className={`text-right font-['JetBrains_Mono'] font-bold ${isTop ? "text-green-400" : "text-[#CCFF00]"}`}>{r.points}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{r.matches}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-green-400">{r.wins}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-red-400">{r.losses}</TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      {/* Global Ranking */}
      <div className="card-tactical p-6" data-testid="global-ranking">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Ranking</p>
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight">
              {rankingCategory === "all" ? "Global de Jugadores" : `Categoria ${CATEGORIES.find(c => c.value === rankingCategory)?.label || rankingCategory}`}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="ranking-category-tabs">
          <button onClick={() => setRankingCategory("all")} className={`px-3 h-8 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${rankingCategory === "all" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`} data-testid="rank-cat-all">Global</button>
          {CATEGORIES.map((c) => (
            <button key={c.value} onClick={() => setRankingCategory(c.value)} className={`px-3 h-8 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${rankingCategory === c.value ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`} data-testid={`rank-cat-${c.value}`}>
              {c.short}
            </button>
          ))}
        </div>
        {ranking.length === 0 ? (
          <p className="text-gray-500 text-sm">{rankingCategory === "all" ? "No hay estadisticas disponibles" : "Sin jugadores con esta categoria aun"}</p>
        ) : (
          <div className="overflow-x-auto -mx-4 px-4" data-testid="global-ranking-scroll">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5">
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">#</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Jugador</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Cat.</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider">Estado</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Puntos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Partidos</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Victorias</TableHead>
                <TableHead className="text-gray-500 text-xs uppercase tracking-wider text-right">Perfil</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ranking.map((r, i) => {
                const isTop = i < 3;
                const nameCls = isTop ? "font-semibold text-green-400" : "font-semibold text-white";
                const posCls = isTop ? "font-['Barlow_Condensed'] text-xl font-black text-green-400" : "font-['Barlow_Condensed'] text-xl font-black text-gray-600";
                return (
                <TableRow key={r.user_id || i} className={`border-white/5 hover:bg-white/5 ${isTop ? "bg-green-500/5" : ""}`} data-testid={`ranking-row-${i}`}>
                  <TableCell className={posCls}>{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell><span className={nameCls}>{r.name || `Jugador`}</span></TableCell>
                  <TableCell><span className="text-[#CCFF00] text-xs font-['JetBrains_Mono']">{r.category ? categoryShort(r.category) : "—"}</span></TableCell>
                  <TableCell><span className="text-gray-400 text-xs">{r.state || "—"}</span></TableCell>
                  <TableCell className={`text-right font-['JetBrains_Mono'] font-bold ${isTop ? "text-green-400" : "text-[#CCFF00]"}`}>{r.points || 0}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{r.games_played || 0}</TableCell>
                  <TableCell className="text-right font-['JetBrains_Mono'] text-gray-400">{r.games_won || 0}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/dashboard/jugador/${r.user_id}`} className="inline-flex items-center text-gray-400 hover:text-[#CCFF00] transition-colors" data-testid={`profile-link-${i}`}>
                      <UserCircle size={18} />
                    </Link>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function ExportPdfMenu() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const download = async (source, label) => {
    setBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/statistics/pdf-report?source=${source}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (!r.ok) throw new Error("pdf failed");
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = u; a.download = `dyd-reporte-${source}.pdf`; a.click();
      URL.revokeObjectURL(u);
      toast.success(`PDF de ${label} descargado`);
    } catch { toast.error("Error al generar PDF"); }
    finally { setBusy(false); setOpen(false); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 bg-[#CCFF00] text-black hover:bg-[#B3E600] px-4 h-10 text-xs font-bold uppercase tracking-wider transition-colors" data-testid="pdf-report-btn">
        <ChartBar size={14} /> Exportar PDF
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-20 bg-[#141414] border border-white/10 min-w-[220px] shadow-2xl" data-testid="pdf-export-menu">
          <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-gray-500">Elige fuente</p>
          {[
            { s: "all", label: "Global (todo)" },
            { s: "tournament", label: "Solo Torneos" },
            { s: "reta", label: "Solo Retas" },
            { s: "rental", label: "Solo Canchas" },
          ].map((opt) => (
            <button
              key={opt.s}
              disabled={busy}
              onClick={() => download(opt.s, opt.label)}
              className="w-full text-left px-3 py-2 text-xs text-white hover:bg-[#CCFF00]/10 hover:text-[#CCFF00] disabled:opacity-40 border-t border-white/5"
              data-testid={`pdf-source-${opt.s}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

